import {
  Input,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  Text,
  type IconName,
} from "@salestrak/ui";
import {
  UserSelector,
  AssigneeSelector,
  TeamSelector,
  RoleSelector,
  WorkflowSelector,
} from "#app/components/entity-selectors";
import type { FilterFieldType } from "#app/lib/filters/types";
import { DateInput } from "./inputs/date-input";
import { IssueTypeInput } from "./inputs/issue-type-input";
import { StageInput } from "./inputs/stage-input";
import { AgeStatusInput } from "./inputs/age-status-input";
import { AgeStatusSelect } from "./inputs/age-status-select";
import { WorkflowStageInput } from "./inputs/workflow-stage-input";
import { LastUpdatedInput } from "./inputs/last-updated-input";
import { MoreOptionsInput } from "./inputs/more-options-input";
import type { Omittable } from "@salestrak/ui";
type FilterValueInputProps = Omittable<{
  fieldType: FilterFieldType;
  value: unknown;
  onChange: (value: unknown) => void;
  multiple?: boolean;
  /** "quick" renders checkbox/inline inputs, "advanced" renders dropdown selects. */
  variant?: "quick" | "advanced";
  /** Leading icon shown inside select-style triggers. */
  icon?: IconName;
}>;
const BOOLEAN_OPTIONS = [
  { value: "true", label: "True" },
  { value: "false", label: "False" },
];

/**
 * Dynamic value input component resolver.
 *
 * Renders the appropriate input based on the field type. Entity types
 * (user, team, assignee) use their respective EntitySelector
 * wrappers; role uses RoleSelector.
 */
export function FilterValueInput({
  fieldType,
  value,
  onChange,
  variant = "quick",
  icon,
}: FilterValueInputProps) {
  const advancedSelectSize = "xs";
  // The Input size scale bottoms out at "sm" (Select goes one step smaller).
  const advancedInputSize = variant === "advanced" ? "sm" : "default";
  // Quick filters stack one control per row and span the panel. Advanced rows
  // sit in a flex row beside a field and operator, so their triggers stay
  // content-sized. `Input`/`SelectTrigger`/`DatePicker` are full width already;
  // only the entity triggers need telling.
  const fillTrigger = variant === "quick";

  switch (fieldType) {
    case "string":
    case "email":
      return (
        <Input
          value={typeof value === "string" ? value : ""}
          onChange={(event) => onChange(event.target.value)}
          placeholder={fieldType === "email" ? "Enter email..." : "Enter text..."}
          size={advancedInputSize}
        />
      );

    case "number":
      return (
        <Input
          value={value != null ? String(value) : ""}
          onChange={(event) => {
            const text = event.target.value;
            const num = Number(text);
            onChange(text === "" ? null : Number.isNaN(num) ? text : num);
          }}
          placeholder="Enter number..."
          inputMode="numeric"
          size={advancedInputSize}
        />
      );

    case "boolean": {
      const boolStr = value === true ? "true" : value === false ? "false" : null;

      return (
        <Select
          items={BOOLEAN_OPTIONS}
          value={boolStr}
          onValueChange={(next: string | null) =>
            onChange(next === "true" ? true : next === "false" ? false : null)
          }
        >
          <SelectTrigger size="sm">
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectPopup>
            {BOOLEAN_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                <Text>{opt.label}</Text>
              </SelectItem>
            ))}
          </SelectPopup>
        </Select>
      );
    }

    case "date":
      return <DateInput value={value} onChange={onChange} />;

    case "entity-user":
      return (
        <UserSelector
          value={value as string[] | null}
          onValueChange={(ids) => onChange(ids.length > 0 ? ids : null)}
          size={advancedSelectSize}
          chips
          contentFit
          placeholder="Search members..."
          icon={icon}
          fill={fillTrigger}
          activeOnly
        />
      );

    case "entity-assignee":
      return (
        <AssigneeSelector
          value={value as string[] | null}
          onValueChange={(ids) => onChange(ids.length > 0 ? ids : null)}
          size={advancedSelectSize}
          chips
          contentFit
          placeholder="Search members..."
          allowSetPrimary={false}
          showPrimarySubtext={false}
          icon={icon}
          fill={fillTrigger}
          activeOnly
        />
      );

    case "entity-team":
      return (
        <TeamSelector
          value={value as string[] | null}
          onValueChange={(ids) => onChange(ids.length > 0 ? ids : null)}
          size={advancedSelectSize}
          chips
          contentFit
          placeholder="Search teams..."
          icon={icon}
          fill={fillTrigger}
        />
      );

    case "role":
      return (
        <RoleSelector
          value={value ? String(value) : null}
          onValueChange={(id) => onChange(id ? [id] : null)}
          size={advancedSelectSize}
          chips
          placeholder="Select role..."
          icon={icon}
          fill={fillTrigger}
        />
      );

    case "issue-type":
      return <IssueTypeInput value={value} onChange={onChange} icon={icon} fill={fillTrigger} />;

    case "workflow":
      return (
        <WorkflowSelector
          value={value as string[] | null}
          onValueChange={(ids) => onChange(ids.length > 0 ? ids : null)}
          maxSelections={0}
          size={advancedSelectSize}
          chips
          contentFit
          placeholder="Select workflows..."
          icon={icon}
          fill={fillTrigger}
        />
      );

    case "stage":
      return <StageInput value={value} onChange={onChange} icon={icon} fill={fillTrigger} />;

    case "age-status":
      return variant === "advanced"
        ? <AgeStatusSelect value={value} onChange={onChange} icon={icon} />
        : <AgeStatusInput value={value} onChange={onChange} />;

    case "workflow-stage":
      return <WorkflowStageInput value={value} onChange={onChange} icon={icon} fill={fillTrigger} />;

    case "last-updated":
      return <LastUpdatedInput value={value} onChange={onChange} icon={icon} />;

    case "more-options":
      return <MoreOptionsInput value={value} onChange={onChange} />;

    case "select":
    case "multi-select":
    default:
      return (
        <Input
          value={typeof value === "string" ? value : ""}
          onChange={(event) => onChange(event.target.value || null)}
          placeholder="Enter value..."
          size={advancedInputSize}
        />
      );
  }
}
